import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EquipmentDto, CreateEquipmentDto, UpdateEquipmentDto, EquipmentType, EquipmentStatus, EquipmentTypeTemplateDto } from '../../types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { Upload, X, Eye, Download, Sparkles, Trash2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { getEquipmentPhotoUrl } from '../../utils/formatters';
import EquipmentTemplateBrowser from '../equipment/EquipmentTemplateBrowser';
import SearchableSelect from '../ui/SearchableSelect';
import ImageThumbnail from '../common/ImageThumbnail';

interface EquipmentFormProps {
	equipment?: EquipmentDto | null;
	onSubmit: (data: CreateEquipmentDto | UpdateEquipmentDto) => void;
	isLoading?: boolean;
	error?: string;
	onPendingPhotoChange?: (file: File | null) => void;
	onFormDirtyChange?: (isDirty: boolean) => void;
}

const equipmentSchema = yup.object({
	selectedClientId: yup.number().required('Client is required'),
	clientLocationId: yup.number().required('Location is required'),
	unitId: yup.number().nullable(),
	equipmentType: yup
		.mixed<number>()
		.required('Equipment type is required')
		.transform((value) => {
			// If already a number, return it
			if (typeof value === 'number' && !isNaN(value)) {
				return value;
			}
			// If string, try to parse it (but not empty string)
			if (typeof value === 'string' && value !== '') {
				const parsed = parseInt(value, 10);
				if (!isNaN(parsed)) {
					return parsed;
				}
			}
			// Return undefined if empty/invalid (will trigger required validation)
			return undefined;
		})
		.test('is-number', 'Equipment type is required', (value) => value !== undefined && value !== null && typeof value === 'number' && !isNaN(value)),
	customEquipmentType: yup.string().max(100, 'Custom type must be less than 100 characters'),
	name: yup.string().required('Name is required').max(200, 'Name must be less than 200 characters'),
	make: yup.string().max(100, 'Make must be less than 100 characters'),
	model: yup.string().max(100, 'Model must be less than 100 characters'),
	serialNumber: yup.string().max(100, 'Serial number must be less than 100 characters'),
	installDate: yup.string().nullable(),
	expectedLifespanYears: yup.number().min(1).max(100).nullable(),
	replacementCost: yup.number().min(0).nullable(),
	status: yup
		.mixed<number>()
		.required('Status is required')
		.transform((value) => {
			// If already a number, return it
			if (typeof value === 'number' && !isNaN(value)) {
				return value;
			}
			// If string, try to parse it
			if (typeof value === 'string') {
				const parsed = parseInt(value, 10);
				if (!isNaN(parsed)) {
					return parsed;
				}
			}
			// Default fallback
			return EquipmentStatus.Active;
		})
		.test('is-number', 'Status must be a number', (value) => typeof value === 'number' && !isNaN(value)),
	warrantyExpirationDate: yup.string().nullable(),
	warrantyProvider: yup.string().max(200, 'Warranty provider must be less than 200 characters'),
	notes: yup.string().nullable(),
	photoUrl: yup.string().max(500, 'Photo URL must be less than 500 characters').nullable(),
	manualUrl: yup.string().max(500, 'Manual URL must be less than 500 characters').url('Must be a valid URL').nullable(),
}).required();

const EquipmentForm: React.FC<EquipmentFormProps> = ({ equipment, onSubmit, isLoading = false, error, onPendingPhotoChange, onFormDirtyChange }) => {
	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		trigger,
		formState: { errors, isDirty },
		reset
	} = useForm<any>({
		resolver: yupResolver(equipmentSchema),
		defaultValues: {
			selectedClientId: equipment?.clientId || undefined,
			clientLocationId: equipment?.clientLocationId || undefined,
			unitId: equipment?.unitId || undefined,
			equipmentType: equipment?.equipmentType || undefined,
			customEquipmentType: equipment?.customEquipmentType || '',
			name: equipment?.name || '',
			make: equipment?.make || '',
			model: equipment?.model || '',
			serialNumber: equipment?.serialNumber || '',
			installDate: equipment?.installDate || '',
			expectedLifespanYears: equipment?.expectedLifespanYears || undefined,
			replacementCost: equipment?.replacementCost || undefined,
			status: equipment?.status || EquipmentStatus.Active,
			warrantyExpirationDate: equipment?.warrantyExpirationDate || '',
			warrantyProvider: equipment?.warrantyProvider || '',
			notes: equipment?.notes || '',
			photoUrl: equipment?.photoUrl || '',
			manualUrl: equipment?.manualUrl || '',
		}
	});

	const equipmentType = watch('equipmentType');
	const selectedClientId = watch('selectedClientId');
	const selectedClientLocationId = watch('clientLocationId');
	const photoUrlValue = watch('photoUrl');
	const [photoPreviews, setPhotoPreviews] = useState<Array<{ filePath: string; fileName: string; isDataUrl?: boolean; dataUrl?: string; id?: number; description?: string }>>([]);
	const [photoDescriptions, setPhotoDescriptions] = useState<Map<number | string, string>>(new Map());
	const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const [pendingPhotoFiles, setPendingPhotoFiles] = useState<File[]>([]);
	const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; fileName: string } | null>(null);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
	const [currentImageName, setCurrentImageName] = useState<string>('');
	const [currentImageDescription, setCurrentImageDescription] = useState<string>('');
	const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();

	// Fetch company to get company type
	const { data: companyResponse } = useQuery({
		queryKey: ['company'],
		queryFn: () => apiService.getCompany(),
	});
	const companyType = companyResponse?.data?.companyType ?? 0; // Default to Facilities
	const isPropertyManagement = companyType === 9; // PropertyManagement = 9

	// Photo delete mutation
	const deletePhotoMutation = useMutation({
		mutationFn: ({ equipmentId, photoId }: { equipmentId: number; photoId: number }) => {
			return apiService.deleteEquipmentPhoto(equipmentId, photoId);
		},
		onSuccess: (_, { photoId }) => {
			toast.success('Photo deleted successfully!');
			setPhotoPreviews(prev => prev.filter(p => p.id !== photoId));
			queryClient.invalidateQueries({ queryKey: ['equipment', equipment?.id] });
		},
		onError: (error: any) => {
			toast.error(error.response?.data?.message || 'Failed to delete photo');
		}
	});

	// Photo upload mutation (only available when editing existing equipment)
	const uploadPhotoMutation = useMutation({
		mutationFn: (file: File) => {
			if (!equipment?.id) {
				throw new Error('Equipment must be saved before uploading photos');
			}
			return apiService.uploadEquipmentPhoto(equipment.id, file);
		},
		onSuccess: (response) => {
			// Refetch equipment to get updated photos array with IDs
			queryClient.invalidateQueries({ queryKey: ['equipment', equipment?.id] });
		},
		onError: (error: any) => {
			toast.error(error.response?.data?.message || 'Failed to upload photo');
		}
	});

	// Fetch clients
	const { data: clientsResponse } = useQuery({
		queryKey: ['clients'],
		queryFn: () => apiService.getClients(1, 1000),
	});

	const clients = clientsResponse?.data?.data || [];

	// Fetch locations for selected client only
	const { data: locationsResponse, isLoading: locationsLoading } = useQuery({
		queryKey: ['client-locations', selectedClientId],
		queryFn: async () => {
			if (!selectedClientId) return [];
			try {
				const res = await apiService.getClientLocations(selectedClientId);
				// Ensure we always return an array
				const data = res?.data;
				return Array.isArray(data) ? data : [];
			} catch (error) {
				console.error('Error fetching client locations:', error);
				return [];
			}
		},
		enabled: !!selectedClientId,
		staleTime: 60_000,
	});

	// Ensure locations is always an array
	const locations = Array.isArray(locationsResponse) ? locationsResponse : [];

	// Fetch units for selected location (only for Property Management companies)
	const { data: unitsResponse, isLoading: unitsLoading } = useQuery({
		queryKey: ['units-by-location', selectedClientLocationId],
		queryFn: async () => {
			if (!selectedClientLocationId) return [];
			try {
				const res = await apiService.getUnitsByLocation(selectedClientLocationId);
				// Ensure we always return an array
				const data = res?.data;
				return Array.isArray(data) ? data : [];
			} catch (error) {
				console.error('Error fetching units:', error);
				return [];
			}
		},
		enabled: !!selectedClientLocationId && isPropertyManagement,
		staleTime: 60_000,
	});

	// Ensure units is always an array
	const units = Array.isArray(unitsResponse) ? unitsResponse : [];

	// Track form dirty state and notify parent
	useEffect(() => {
		onFormDirtyChange?.(isDirty);
	}, [isDirty, onFormDirtyChange]);

	const handleFormSubmit = (data: any) => {
		// Convert empty strings to undefined for optional fields
		// Remove selectedClientId as it's not part of the DTO
		const { selectedClientId, ...formData } = data;
		const cleanedData: CreateEquipmentDto | UpdateEquipmentDto = {
			...formData,
			customEquipmentType: formData.customEquipmentType || undefined,
			make: formData.make || undefined,
			model: formData.model || undefined,
			serialNumber: formData.serialNumber || undefined,
			installDate: formData.installDate || undefined,
			expectedLifespanYears: formData.expectedLifespanYears || undefined,
			replacementCost: formData.replacementCost || undefined,
			warrantyExpirationDate: formData.warrantyExpirationDate || undefined,
			warrantyProvider: formData.warrantyProvider || undefined,
			notes: formData.notes || undefined,
			photoUrl: formData.photoUrl || undefined,
			manualUrl: formData.manualUrl || undefined,
		};
		onSubmit(cleanedData);
	};

	// Reset form when equipment prop changes
	useEffect(() => {
		if (equipment) {
			reset({
				selectedClientId: equipment.clientId || undefined,
				clientLocationId: equipment.clientLocationId,
				equipmentType: equipment.equipmentType,
				customEquipmentType: equipment.customEquipmentType || '',
				name: equipment.name,
				make: equipment.make || '',
				model: equipment.model || '',
				serialNumber: equipment.serialNumber || '',
				installDate: equipment.installDate || '',
				expectedLifespanYears: equipment.expectedLifespanYears || undefined,
				replacementCost: equipment.replacementCost || undefined,
				status: equipment.status,
				warrantyExpirationDate: equipment.warrantyExpirationDate || '',
				warrantyProvider: equipment.warrantyProvider || '',
				notes: equipment.notes || '',
				photoUrl: equipment.photoUrl || '',
				manualUrl: equipment.manualUrl || '',
			});
			// Load existing photos from the Photos array
			if (equipment.photos && equipment.photos.length > 0) {
				const photos = equipment.photos.map(p => ({
					filePath: p.filePath,
					fileName: p.fileName,
					id: p.id,
					description: p.description || ''
				}));
				setPhotoPreviews(photos);
				// Initialize descriptions map
				const descriptionsMap = new Map<number | string, string>();
				photos.forEach(p => {
					if (p.id) {
						descriptionsMap.set(p.id, p.description || '');
					}
				});
				setPhotoDescriptions(descriptionsMap);
			} else {
				setPhotoPreviews([]);
				setPhotoDescriptions(new Map());
			}
		} else {
			reset({
				selectedClientId: undefined,
				clientLocationId: undefined,
				equipmentType: undefined,
				customEquipmentType: '',
				name: '',
				make: '',
				model: '',
				serialNumber: '',
				installDate: '',
				expectedLifespanYears: undefined,
				replacementCost: undefined,
				status: EquipmentStatus.Active,
				warrantyExpirationDate: '',
				warrantyProvider: '',
				notes: '',
				photoUrl: '',
				manualUrl: '',
			});
			setPhotoPreviews([]);
			setPendingPhotoFiles([]);
			onPendingPhotoChange?.(null);
		}
	}, [equipment, reset, onPendingPhotoChange]);

	const validateAndUploadMultiple = (files: File[]) => {
		const validFiles: File[] = [];
		
		for (const file of files) {
			if (!file.type.startsWith('image/')) {
				toast.error(`${file.name} is not an image file`);
				continue;
			}
			if (file.size > 5 * 1024 * 1024) {
				toast.error(`${file.name} is too large (max 5MB)`);
				continue;
			}
			validFiles.push(file);
		}
		
		if (validFiles.length === 0) return;
		
		if (equipment?.id) {
			// Upload immediately if editing existing equipment
			setIsUploadingPhoto(true);
			setUploadProgress({ current: 0, total: validFiles.length, fileName: validFiles[0].name });
			let completedCount = 0;
			let successCount = 0;
			// Upload files one by one
			validFiles.forEach((file, index) => {
				// Add a small delay between uploads to avoid overwhelming the server
				setTimeout(() => {
					setUploadProgress({ current: index, total: validFiles.length, fileName: file.name });
					uploadPhotoMutation.mutate(file, {
						onSuccess: () => {
							successCount++;
							completedCount++;
							setUploadProgress({ current: completedCount, total: validFiles.length, fileName: file.name });
							if (completedCount === validFiles.length) {
								// Clear state immediately to hide overlay
								setIsUploadingPhoto(false);
								setUploadProgress(null);
								// Reset file input
								if (fileInputRef.current) {
									fileInputRef.current.value = '';
								}
								// Delay query invalidation to avoid race condition
								setTimeout(() => {
									if (successCount > 0) {
										toast.success(`${successCount} photo(s) uploaded successfully!`);
										// Refetch equipment data to get updated photos
										queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id] });
									}
								}, 100);
							}
						},
						onError: () => {
							completedCount++;
							setUploadProgress({ current: completedCount, total: validFiles.length, fileName: file.name });
							if (completedCount === validFiles.length) {
								// Clear state immediately to hide overlay
								setIsUploadingPhoto(false);
								setUploadProgress(null);
								// Reset file input
								if (fileInputRef.current) {
									fileInputRef.current.value = '';
								}
								// Delay query invalidation to avoid race condition
								setTimeout(() => {
									if (successCount > 0) {
										toast.success(`${successCount} photo(s) uploaded successfully!`);
										// Refetch equipment data to get updated photos
										queryClient.invalidateQueries({ queryKey: ['equipment', equipment.id] });
									}
								}, 100);
							}
						}
					});
				}, index * 100);
			});
		} else {
			// For new equipment, store files to upload after equipment is created
			setPendingPhotoFiles(validFiles);
			onPendingPhotoChange?.(validFiles[0]); // Pass first file for now
			// Show previews of all files
			const newPreviews: Array<{ filePath: string; fileName: string; isDataUrl: boolean; dataUrl: string }> = [];
			validFiles.forEach((file, index) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					newPreviews.push({
						filePath: `pending-${index}`,
						fileName: file.name,
						isDataUrl: true,
						dataUrl: e.target?.result as string
					});
					if (newPreviews.length === validFiles.length) {
						setPhotoPreviews(newPreviews);
					}
				};
				reader.readAsDataURL(file);
			});
		}
	};

	const validateAndUpload = (file?: File) => {
		if (file) {
			validateAndUploadMultiple([file]);
		}
	};

	const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length > 0) {
			validateAndUploadMultiple(files);
		}
	};

	const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			validateAndUploadMultiple(files);
		}
	};

	const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	};

	const handleRemovePhoto = (photoPath: string, photoId?: number) => {
		if (equipment?.id && photoId) {
			// Delete from server if it's an existing photo
			deletePhotoMutation.mutate({ equipmentId: equipment.id, photoId });
		} else {
			// Just remove from preview if it's a new upload
			setPhotoPreviews(prev => prev.filter(p => p.filePath !== photoPath));
			if (photoPath.startsWith('pending-')) {
				const index = parseInt(photoPath.replace('pending-', ''));
				setPendingPhotoFiles(prev => prev.filter((_, i) => i !== index));
			}
		}
	};

	const handleRemoveLegacyPhoto = () => {
		// The legacy single photo lives on the equipment.photoUrl field.
		// Clear the form value so saving the form persists the removal (backend maps null -> cleared).
		setValue('photoUrl', '', { shouldDirty: true, shouldValidate: true });
		toast('Legacy photo removed. Click "Update Equipment" to save the change.', { icon: '🗑️' });
	};

	const handleViewImage = useCallback(async (filePath: string, fileName: string, description?: string) => {
		try {
			const blob = await apiService.downloadEquipmentPhoto(filePath);
			const url = window.URL.createObjectURL(blob);
			setCurrentImageUrl(url);
			setCurrentImageName(fileName);
			setCurrentImageDescription(description || '');
			setImageModalOpen(true);
		} catch (error) {
			toast.error('Failed to load image');
			console.error('Error loading image:', error);
		}
	}, []);

	const handleDownloadImage = useCallback(async (filePath: string, fileName: string) => {
		try {
			const blob = await apiService.downloadEquipmentPhoto(filePath);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			toast.error('Failed to download file');
			console.error('Error downloading file:', error);
		}
	}, []);

	const closeImageModal = useCallback(() => {
		setImageModalOpen(false);
		if (currentImageUrl && currentImageUrl.startsWith('blob:')) {
			window.URL.revokeObjectURL(currentImageUrl);
		}
		setCurrentImageUrl('');
		setCurrentImageName('');
		setCurrentImageDescription('');
	}, [currentImageUrl]);

	const getEquipmentTypeDisplay = (type: EquipmentType): string => {
		const typeMap: Record<EquipmentType, string> = {
			[EquipmentType.HVAC]: 'HVAC',
			[EquipmentType.Refrigeration]: 'Refrigeration',
			[EquipmentType.Plumbing]: 'Plumbing',
			[EquipmentType.Electrical]: 'Electrical',
			[EquipmentType.FireSafety]: 'Fire Safety',
			[EquipmentType.Kitchen]: 'Kitchen',
			[EquipmentType.Lighting]: 'Lighting',
			[EquipmentType.BuildingSystems]: 'Building Systems',
			[EquipmentType.Other]: 'Other',
		};
		return typeMap[type] || 'Unknown';
	};

	const getStatusDisplay = (status: EquipmentStatus): string => {
		const statusMap: Record<EquipmentStatus, string> = {
			[EquipmentStatus.Active]: 'Active',
			[EquipmentStatus.Retired]: 'Retired',
			[EquipmentStatus.UnderWarranty]: 'Under Warranty',
		};
		return statusMap[status] || 'Unknown';
	};

	const handleTemplateSelect = (template: EquipmentTypeTemplateDto) => {
		// Pre-fill form with template values
		// Ensure equipmentType is a number
		let equipmentTypeValue: number;
		
		if (template.equipmentType === undefined || template.equipmentType === null) {
			console.error('Template equipment type is missing:', template);
			toast.error('Template is missing equipment type');
			return;
		}
		
		if (typeof template.equipmentType === 'number') {
			equipmentTypeValue = template.equipmentType;
		} else if (typeof template.equipmentType === 'string') {
			// Check if it's a numeric string first
			const parsed = parseInt(template.equipmentType, 10);
			if (!isNaN(parsed)) {
				equipmentTypeValue = parsed;
			} else {
				// It's an enum name string (e.g., "BuildingSystems"), map it to the numeric value
				const enumKey = template.equipmentType as keyof typeof EquipmentType;
				if (enumKey in EquipmentType) {
					const enumValue = EquipmentType[enumKey];
					if (typeof enumValue === 'number') {
						equipmentTypeValue = enumValue;
					} else {
						console.error('Invalid equipment type enum key:', template.equipmentType);
						toast.error('Invalid equipment type in template');
						return;
					}
				} else {
					console.error('Invalid equipment type string:', template.equipmentType);
					toast.error('Invalid equipment type in template');
					return;
				}
			}
		} else {
			// Try to convert to number
			const converted = Number(template.equipmentType);
			if (isNaN(converted)) {
				console.error('Invalid equipment type from template:', template.equipmentType, typeof template.equipmentType);
				toast.error('Invalid equipment type in template');
				return;
			}
			equipmentTypeValue = converted;
		}
		
		// Validate the equipment type value is a valid enum value
		const validTypes = Object.values(EquipmentType).filter(v => typeof v === 'number');
		if (!validTypes.includes(equipmentTypeValue)) {
			console.error('Equipment type value not in enum:', equipmentTypeValue, 'Valid types:', validTypes);
			toast.error('Invalid equipment type value');
			return;
		}
		
		// Use setValue with all options to ensure proper update
		setValue('equipmentType', equipmentTypeValue, { 
			shouldValidate: true, 
			shouldDirty: true,
			shouldTouch: true
		});
		setValue('name', template.name, { shouldValidate: true, shouldDirty: true });
		if (template.expectedLifespanYears) {
			setValue('expectedLifespanYears', template.expectedLifespanYears, { shouldValidate: true, shouldDirty: true });
		}
		// Use the midpoint of replacement cost range if available
		if (template.typicalReplacementCostMin && template.typicalReplacementCostMax) {
			const midpoint = (template.typicalReplacementCostMin + template.typicalReplacementCostMax) / 2;
			setValue('replacementCost', Math.round(midpoint), { shouldValidate: true, shouldDirty: true });
		} else if (template.typicalReplacementCostMin) {
			setValue('replacementCost', template.typicalReplacementCostMin, { shouldValidate: true, shouldDirty: true });
		} else if (template.typicalReplacementCostMax) {
			setValue('replacementCost', template.typicalReplacementCostMax, { shouldValidate: true, shouldDirty: true });
		}
		// Trigger validation after a small delay to ensure state updates
		setTimeout(() => {
			trigger('equipmentType');
		}, 10);
		toast.success(`Template "${template.name}" applied!`);
	};

	return (
		<div className="relative">
			{/* Form-wide loading overlay */}
			{isUploadingPhoto && (
				<div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
					<div className="text-center bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color mx-auto mb-4"></div>
						{uploadProgress && (
							<div className="space-y-3">
								<p className="text-lg font-medium text-gray-900 dark:text-gray-100">
									Uploading Photos
								</p>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{uploadProgress.current} of {uploadProgress.total}
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-500 truncate max-w-xs">
									{uploadProgress.fileName}
								</p>
								<div className="w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
									<div
										className="bg-accent-color h-2 rounded-full transition-all duration-300"
										style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
									></div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			
			<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
				{error && (
					<div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
						<div className="flex">
							<div className="ml-3">
								<h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
								<div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
							</div>
						</div>
					</div>
				)}

				{/* Quick Add from Template Button - Only show when creating new equipment */}
				{!equipment && (
					<div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
						<div>
							<h3 className="text-sm font-medium text-blue-900 dark:text-blue-200">Quick Add from Template</h3>
							<p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
								Select a template to pre-fill equipment details with industry-standard values
							</p>
						</div>
						<button
							type="button"
							onClick={() => setTemplateBrowserOpen(true)}
							className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
						>
							<Sparkles className="w-4 h-4" />
							Browse Templates
						</button>
					</div>
				)}

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{/* Client and Location Selection - Same Row, 50/50 */}
				<div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
					{/* Client Selection */}
					<div>
						<label htmlFor="selectedClientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							Client <span className="text-red-500">*</span>
						</label>
						<Controller
							name="selectedClientId"
							control={control}
							render={({ field }) => (
								<SearchableSelect
									options={clients.map((client: any) => ({
										value: client.id,
										label: client.name
									}))}
									value={field.value}
									onChange={(value) => {
										field.onChange(value);
										// Clear location when client changes
										if (value !== field.value) {
											setValue('clientLocationId', undefined);
										}
									}}
									placeholder="Select a client"
									searchPlaceholder="Search clients..."
									error={errors.selectedClientId ? String(errors.selectedClientId?.message) : undefined}
								/>
							)}
						/>
					</div>

					{/* Location Selection */}
					<div>
						<label htmlFor="clientLocationId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							Location <span className="text-red-500">*</span>
						</label>
						<Controller
							name="clientLocationId"
							control={control}
							render={({ field }) => (
								<select
									{...field}
									id="clientLocationId"
									className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={!selectedClientId || locationsLoading}
								>
									<option value="">
										{!selectedClientId
											? 'Select a client first'
											: locationsLoading 
												? 'Loading locations...' 
												: locations.length === 0 
													? 'No locations available. Please create a location for this client first.' 
													: 'Select a location'}
									</option>
									{locations.map((location: any) => (
										<option key={location.id} value={location.id}>
											{location.locationName}
										</option>
									))}
								</select>
							)}
						/>
						{errors.clientLocationId && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.clientLocationId?.message)}</p>
						)}
					</div>

					{/* Unit Selection - Only for Property Management companies */}
					{isPropertyManagement && (
					<div>
						<label htmlFor="unitId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							Unit (Optional)
						</label>
						<Controller
							name="unitId"
							control={control}
							render={({ field }) => (
								<select
									{...field}
									id="unitId"
									value={field.value || ''}
									onChange={(e) => field.onChange(e.target.value === '' ? undefined : parseInt(e.target.value))}
									className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={!selectedClientLocationId || unitsLoading}
								>
									<option value="">
										{!selectedClientLocationId
											? 'Select a location first'
											: unitsLoading
												? 'Loading units...'
												: units.length === 0
													? 'No units available'
													: 'Select a unit (optional)'}
									</option>
									{units.map((unit: any) => (
										<option key={unit.unitId} value={unit.unitId}>
											Unit {unit.unitNumber} {unit.unitType ? `(${unit.unitType})` : ''}
										</option>
									))}
								</select>
							)}
						/>
					</div>
					)}
				</div>

				{/* Equipment Type */}
				<div>
					<label htmlFor="equipmentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Equipment Type <span className="text-red-500">*</span>
					</label>
					<Controller
						name="equipmentType"
						control={control}
						render={({ field }) => {
							// Use watched value to ensure updates are reflected immediately
							// The watched value should update when setValue is called
							const formValue = equipmentType !== undefined ? equipmentType : field.value;
							// Convert to number if it's a valid value, otherwise use empty string for placeholder
							let currentValue: number | string = '';
							if (formValue !== undefined && formValue !== null && !isNaN(Number(formValue))) {
								currentValue = Number(formValue);
							}
							
							// Get all equipment types, sort alphabetically, but keep "Other" at the bottom
							const equipmentTypes = Object.values(EquipmentType)
								.filter(v => typeof v === 'number')
								.map(type => ({
									value: type,
									label: getEquipmentTypeDisplay(type as EquipmentType)
								}))
								.sort((a, b) => {
									// Keep "Other" at the bottom
									if (a.value === EquipmentType.Other) return 1;
									if (b.value === EquipmentType.Other) return -1;
									// Sort others alphabetically
									return a.label.localeCompare(b.label);
								});

							return (
								<select
									key={`equipment-type-${currentValue}`}
									id="equipmentType"
									name={field.name}
									value={currentValue}
									onBlur={field.onBlur}
									ref={field.ref}
									onChange={(e) => {
										const value = e.target.value;
										if (value === '') {
											field.onChange(undefined);
										} else {
											const numValue = Number(value);
											if (!isNaN(numValue)) {
												field.onChange(numValue);
											}
										}
									}}
									className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
								>
									<option value="">Please select...</option>
									{equipmentTypes.map(({ value, label }) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							);
						}}
					/>
					{errors.equipmentType && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.equipmentType.message)}</p>
					)}
				</div>

				{/* Custom Equipment Type (only if Other) */}
				{equipmentType === EquipmentType.Other && (
					<div>
						<label htmlFor="customEquipmentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Custom Equipment Type
						</label>
						<input
							{...register('customEquipmentType')}
							type="text"
							id="customEquipmentType"
							className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
							placeholder="Enter custom equipment type"
						/>
						{errors.customEquipmentType && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.customEquipmentType?.message)}</p>
						)}
					</div>
				)}

				{/* Name */}
				<div className="md:col-span-2">
					<label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Name <span className="text-red-500">*</span>
					</label>
					<input
						{...register('name')}
						type="text"
						id="name"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter equipment name"
					/>
					{errors.name && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.name?.message)}</p>
					)}
				</div>

				{/* Make */}
				<div>
					<label htmlFor="make" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Make
					</label>
					<input
						{...register('make')}
						type="text"
						id="make"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter make"
					/>
					{errors.make && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.make?.message)}</p>
					)}
				</div>

				{/* Model */}
				<div>
					<label htmlFor="model" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Model
					</label>
					<input
						{...register('model')}
						type="text"
						id="model"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter model"
					/>
					{errors.model && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.model?.message)}</p>
					)}
				</div>

				{/* Serial Number */}
				<div>
					<label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Serial Number
					</label>
					<input
						{...register('serialNumber')}
						type="text"
						id="serialNumber"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter serial number"
					/>
					{errors.serialNumber && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.serialNumber?.message)}</p>
					)}
				</div>

				{/* Install Date */}
				<div>
					<label htmlFor="installDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Install Date
					</label>
					<input
						{...register('installDate')}
						type="date"
						id="installDate"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.installDate && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.installDate?.message)}</p>
					)}
				</div>

				{/* Expected Lifespan */}
				<div>
					<label htmlFor="expectedLifespanYears" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Expected Lifespan (Years)
					</label>
					<input
						{...register('expectedLifespanYears', { valueAsNumber: true })}
						type="number"
						id="expectedLifespanYears"
						min="1"
						max="100"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter expected lifespan"
					/>
					{errors.expectedLifespanYears && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.expectedLifespanYears?.message)}</p>
					)}
				</div>

				{/* Replacement Cost */}
				<div>
					<label htmlFor="replacementCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Replacement Cost
					</label>
					<Controller
						name="replacementCost"
						control={control}
						render={({ field }) => {
							// Format value for display (with currency formatting)
							const formatCurrencyValue = (value: number | null | undefined): string => {
								if (value === null || value === undefined || isNaN(value)) return '';
								return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
							};

							// Parse currency string to number (remove commas and dollar signs)
							const parseCurrencyValue = (value: string): number | null => {
								if (!value) return null;
								const cleaned = value.replace(/[$,]/g, '');
								const parsed = parseFloat(cleaned);
								return isNaN(parsed) ? null : parsed;
							};

							const displayValue = formatCurrencyValue(field.value);

							return (
								<div className="relative">
									<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
									<input
										type="text"
										id="replacementCost"
										value={displayValue}
										onChange={(e) => {
											const parsed = parseCurrencyValue(e.target.value);
											field.onChange(parsed);
										}}
										onBlur={field.onBlur}
										className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md pl-8 pr-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
										placeholder="0"
									/>
								</div>
							);
						}}
					/>
					{errors.replacementCost && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.replacementCost?.message)}</p>
					)}
				</div>

				{/* Status */}
				<div>
					<label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Status <span className="text-red-500">*</span>
					</label>
					<Controller
						name="status"
						control={control}
						render={({ field }) => {
							// Ensure value is always a valid number
							let currentValue = field.value;
							if (currentValue === undefined || currentValue === null || isNaN(Number(currentValue))) {
								currentValue = EquipmentStatus.Active;
							} else {
								currentValue = Number(currentValue);
							}
							
							return (
								<select
									id="status"
									name={field.name}
									value={currentValue}
									onBlur={field.onBlur}
									ref={field.ref}
									onChange={(e) => {
										const numValue = Number(e.target.value);
										if (!isNaN(numValue)) {
											field.onChange(numValue);
										}
									}}
									className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
								>
									{Object.values(EquipmentStatus).filter(v => typeof v === 'number').map((status) => (
										<option key={status} value={status}>
											{getStatusDisplay(status as EquipmentStatus)}
										</option>
									))}
								</select>
							);
						}}
					/>
					{errors.status && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.status?.message)}</p>
					)}
				</div>

				{/* Warranty Provider */}
				<div>
					<label htmlFor="warrantyProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Warranty Provider
					</label>
					<input
						{...register('warrantyProvider')}
						type="text"
						id="warrantyProvider"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter warranty provider"
					/>
					{errors.warrantyProvider && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.warrantyProvider?.message)}</p>
					)}
				</div>

				{/* Warranty Expiration Date */}
				<div>
					<label htmlFor="warrantyExpirationDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Warranty Expiration Date
					</label>
					<input
						{...register('warrantyExpirationDate')}
						type="date"
						id="warrantyExpirationDate"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.warrantyExpirationDate && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.warrantyExpirationDate?.message)}</p>
					)}
				</div>

				{/* Photo Upload */}
				<div className="md:col-span-2">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Equipment Photos
					</label>

					{/* Legacy single photo (equipment.photoUrl) - older records only. Allow removal. */}
					{equipment?.id && photoUrlValue && (
						<div className="mb-4 p-3 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
							<div className="flex items-start gap-3">
								<div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
									<img
										src={getEquipmentPhotoUrl(photoUrlValue)}
										alt="Legacy equipment photo"
										className="w-full h-full object-cover"
										onError={(e) => {
											(e.currentTarget as HTMLImageElement).style.display = 'none';
										}}
									/>
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-amber-900 dark:text-amber-200">Legacy photo attached</p>
									<p className="text-xs text-amber-700 dark:text-amber-300 mt-1 break-all">{photoUrlValue}</p>
									<p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
										If this photo is broken or unwanted, remove it and upload a new one below.
									</p>
								</div>
								<button
									type="button"
									onClick={handleRemoveLegacyPhoto}
									className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex-shrink-0"
									title="Remove legacy photo"
									aria-label="Remove legacy photo"
								>
									<Trash2 className="h-3.5 w-3.5" />
									Remove
								</button>
							</div>
						</div>
					)}

					{/* Existing Photos Grid */}
					{photoPreviews.length > 0 && (
						<div className="space-y-4 mb-4">
							<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
								{photoPreviews.map((photo, index) => {
									const photoKey = photo.id || photo.filePath || index;
									const description = photoDescriptions.get(photoKey) || photo.description || '';
									
									return (
										<div key={photoKey} className="space-y-2">
											<div className="relative">
												{photo.isDataUrl && photo.dataUrl ? (
													// For new uploads (data URLs), show simple preview
													<div className="relative inline-block w-full">
														<img
															src={photo.dataUrl}
															alt={photo.fileName}
															className="w-full h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
														/>
														<button
															type="button"
															onClick={() => handleRemovePhoto(photo.filePath, photo.id)}
															className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
														>
															<X className="h-4 w-4" />
														</button>
													</div>
												) : (
													// For existing photos, show thumbnail with actions
													<div className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-accent-color dark:hover:border-accent-color transition-colors">
														<div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
															<ImageThumbnail
																filePath={photo.filePath}
																fileName={photo.fileName}
																downloadFn={(path: string) => apiService.downloadEquipmentPhoto(path)}
																loadImmediately={true}
															/>
														</div>
														{/* Action Buttons - always visible in the edit form so photos can be deleted reliably */}
														<div className="absolute top-2 right-2 flex items-center space-x-1 opacity-100 transition-opacity">
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	handleViewImage(photo.filePath, photo.fileName, description);
																}}
																className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
																title="View"
															>
																<Eye className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
															</button>
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	const fileName = photo.filePath.split('/').pop() || `equipment-photo-${equipment?.id || 'new'}.jpg`;
																	handleDownloadImage(photo.filePath, fileName);
																}}
																className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
																title="Download"
															>
																<Download className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
															</button>
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	e.preventDefault();
																	handleRemovePhoto(photo.filePath, photo.id);
																}}
																className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shadow-sm"
																title="Delete"
															>
																<X className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
															</button>
														</div>
													</div>
												)}
											</div>
											{/* Description Input */}
											{equipment?.id && photo.id && (
												<input
													type="text"
													value={description}
													onChange={(e) => {
														const newDescriptions = new Map(photoDescriptions);
														newDescriptions.set(photoKey, e.target.value);
														setPhotoDescriptions(newDescriptions);
													}}
													onBlur={async () => {
														if (photo.id && equipment?.id) {
															const desc = photoDescriptions.get(photoKey) || '';
															try {
																await apiService.updateEquipmentPhotoDescription(equipment.id, photo.id, desc || undefined);
															} catch (error) {
																toast.error('Failed to update photo description');
															}
														}
													}}
													placeholder="Add description..."
													className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
												/>
											)}
										</div>
									);
								})}
							</div>
							{!equipment?.id && (
								<p className="text-xs text-gray-500 dark:text-gray-400">
									Photos will be uploaded after equipment is saved
								</p>
							)}
						</div>
					)}
					
					{/* Upload Area - Always visible when editing */}
					{equipment?.id && (
						<div className="relative">
							{/* Loading Overlay */}
							{isUploadingPhoto && (
								<div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
									<div className="text-center">
										<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color mx-auto mb-4"></div>
										{uploadProgress && (
											<div className="space-y-2">
												<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
													Uploading {uploadProgress.current} of {uploadProgress.total}
												</p>
												<p className="text-xs text-gray-600 dark:text-gray-400">
													{uploadProgress.fileName}
												</p>
												<div className="w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
													<div
														className="bg-accent-color h-2 rounded-full transition-all duration-300"
														style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
													></div>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
							
							<div
								onDrop={handleDrop}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
									isDragOver
										? 'border-accent-color bg-accent-color-50 dark:bg-accent-color-900/20'
										: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
								} ${isUploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}
							>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
									onChange={handlePhotoUpload}
									disabled={isUploadingPhoto}
									className="hidden"
									id="equipment-photo-upload"
								/>
								<div className="space-y-2">
									<div className="mx-auto w-12 h-12 text-gray-400 dark:text-gray-500">
										<Upload className="h-12 w-12 mx-auto" />
									</div>
									<div className="text-sm">
										<p className="text-gray-600 dark:text-gray-300">
											<span className="font-medium text-accent-color">Click to upload</span> or drag and drop
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
											JPEG, PNG, GIF, WEBP up to 5MB
										</p>
									</div>
									<label
										htmlFor="equipment-photo-upload"
										className="inline-block px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 cursor-pointer transition-colors"
									>
										Choose Photos
									</label>
								</div>
							</div>
						</div>
					)}
					
					{/* Upload Area - For new equipment */}
					{!equipment?.id && (
						<div
							onDrop={handleDrop}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
								isDragOver
									? 'border-accent-color bg-accent-color-50 dark:bg-accent-color-900/20'
									: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
							} opacity-60`}
						>
							<input
								ref={fileInputRef}
								type="file"
								multiple
								accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
								onChange={handlePhotoUpload}
								disabled={true}
								className="hidden"
								id="equipment-photo-upload-new"
							/>
							<div className="space-y-2">
								<div className="mx-auto w-12 h-12 text-gray-400 dark:text-gray-500">
									<Upload className="h-12 w-12 mx-auto" />
								</div>
								<div className="text-sm">
									<p className="text-gray-500 dark:text-gray-400">
										Save equipment first to upload photos
									</p>
								</div>
							</div>
						</div>
					)}
					<input type="hidden" {...register('photoUrl')} />
				</div>

				{/* Manual URL */}
				<div>
					<label htmlFor="manualUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Equipment Manual Link
					</label>
					<input
						{...register('manualUrl')}
						type="url"
						id="manualUrl"
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="https://example.com/manual.pdf"
					/>
					{errors.manualUrl && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.manualUrl?.message)}</p>
					)}
				</div>

				{/* Notes */}
				<div className="md:col-span-2">
					<label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Notes
					</label>
					<textarea
						{...register('notes')}
						id="notes"
						rows={4}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="Enter any additional notes"
					/>
					{errors.notes && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.notes?.message)}</p>
					)}
				</div>
			</div>

			<div className="flex justify-end space-x-4 pt-4">
				<button
					type="submit"
					disabled={isLoading}
					className="px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
				>
					{isLoading ? 'Saving...' : equipment ? 'Update Equipment' : 'Create Equipment'}
				</button>
			</div>

			{/* Image Modal */}
			{imageModalOpen && currentImageUrl && (
				<Modal
					isOpen={imageModalOpen}
					onClose={closeImageModal}
					title={currentImageName}
					maxWidth="6xl"
				>
					<div className="space-y-4">
						<div className="flex justify-center items-center h-[calc(100vh-10rem)]">
							<img
								src={currentImageUrl}
								alt={currentImageName}
								className="max-w-full max-h-[calc(100vh-12rem)] object-contain rounded-lg shadow-lg"
							/>
						</div>
						{currentImageDescription && (
							<div className="text-sm text-gray-600 dark:text-gray-400 text-center">
								{currentImageDescription}
							</div>
						)}
					</div>
				</Modal>
			)}
			</form>

			{/* Template Browser Modal */}
			<EquipmentTemplateBrowser
				isOpen={templateBrowserOpen}
				onClose={() => setTemplateBrowserOpen(false)}
				onSelectTemplate={handleTemplateSelect}
				companyType={companyType}
			/>
		</div>
	);
};

export default EquipmentForm;

